import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  useTheme,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Add,
  Search,
  FilterList,
  Edit,
  Delete,
  Visibility,
  Person,
  Business,
  Email,
  Phone,
  LocationOn,
  MoreVert,
  Refresh,
  CalendarToday,
  AttachMoney,
  VpnKey,
  LockOpen,
  Lock,
  ContentCopy,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import ClientDialog from '../../components/Clients/ClientDialog';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import { getApiErrorMessage } from '../../utils/apiError';
import { useAuth } from '../../contexts/AuthContext';
import { userHasPermission } from '../../utils/permissions';
import { useSearchParams } from 'react-router-dom';
import { getAuthToken } from '../../lib/authToken';

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  dateOfBirth: string | null;
  clientType: 'individual' | 'corporation' | 'partnership' | 'llc';
  status: 'active' | 'inactive' | 'prospect';
  portalAccess?: boolean;
  notes: string;
  metadata: any;
  createdAt: string;
  updatedAt: string;
}

interface ClientsResponse {
  success: boolean;
  data: {
    clients: Client[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

const ClientsPage: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManagePortal = userHasPermission(user, 'portal:admin');
  const [filters, setFilters] = useState({
    status: '',
    clientType: '',
    search: '',
  });
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit' | 'create'>('view');
  const [portalCreds, setPortalCreds] = useState<{
    email: string;
    temporaryPassword?: string;
    portalLoginUrl: string;
    inviteUrl?: string;
    emailSent?: boolean;
    clientName: string;
  } | null>(null);

  // Fetch clients — tenantId in key blocks cross-tenant React Query reuse
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['clients', user?.tenantId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`/api/clients?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch clients');
      }
      return response.json();
    },
    enabled: Boolean(user?.tenantId),
    keepPreviousData: true,
  });

  useEffect(() => {
    const create = searchParams.get('create');
    const editId = searchParams.get('edit');
    if (create !== 'true' && !editId) return;

    const next = new URLSearchParams(searchParams);
    next.delete('create');
    next.delete('edit');
    const stripParams = () => setSearchParams(next, { replace: true });

    if (create === 'true') {
      setSelectedClient(null);
      setDialogMode('create');
      setDialogOpen(true);
      stripParams();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = getAuthToken();
        const response = await fetch(`/api/clients/${editId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error('Client not found');
        }
        const json = await response.json();
        if (cancelled) return;
        setSelectedClient(json.data);
        setDialogMode('edit');
        setDialogOpen(true);
      } catch {
        if (!cancelled) toast.error('Could not open that client');
      } finally {
        if (!cancelled) stripParams();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams]);

  // Create client mutation
  const createMutation = useMutation({
    mutationFn: async (clientData: Partial<Client>) => {
      const token = getAuthToken();
      const response = await fetch('/api/clients', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(clientData),
      });
      if (!response.ok) {
        throw new Error('Failed to create client');
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success('Client created successfully');
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setDialogOpen(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Update client mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, clientData }: { id: string; clientData: Partial<Client> }) => {
      const token = getAuthToken();
      const response = await fetch(`/api/clients/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(clientData),
      });
      if (!response.ok) {
        throw new Error('Failed to update client');
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success('Client updated successfully');
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setDialogOpen(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Delete client mutation
  const deleteMutation = useMutation({
    mutationFn: async (clientId: string) => {
      const token = getAuthToken();
      const response = await fetch(`/api/clients/${clientId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to delete client');
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success('Client deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleDelete = (clientId: string) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      deleteMutation.mutate(clientId);
    }
  };

  const portalAction = async (
    client: Client,
    action: 'enable' | 'reset-password' | 'disable'
  ) => {
    const token = getAuthToken();
    const res = await fetch(`/api/clients/${client.id}/portal/${action}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      throw new Error(await getApiErrorMessage(res, `Failed to ${action} portal access`));
    }
    return res.json();
  };

  const portalMutation = useMutation({
    mutationFn: async ({
      client,
      action,
    }: {
      client: Client;
      action: 'enable' | 'reset-password' | 'disable';
    }) => portalAction(client, action),
    onSuccess: (json, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      if (variables.action === 'disable') {
        toast.success('Portal access disabled');
        return;
      }
      const data = json.data || {};
      setPortalCreds({
        email: data.email,
        temporaryPassword: data.temporaryPassword,
        portalLoginUrl: data.portalLoginUrl || '/portal/login',
        inviteUrl: data.inviteUrl,
        emailSent: data.emailSent,
        clientName: `${variables.client.firstName} ${variables.client.lastName}`.trim(),
      });
      toast.success(
        variables.action === 'enable'
          ? 'Portal access enabled'
          : 'Portal password reset'
      );
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleEnablePortal = (client: Client) => {
    if (!client.email) {
      toast.error('Add an email to this client before enabling portal access');
      return;
    }
    const label = client.portalAccess ? 'Reset portal password' : 'Enable portal access';
    if (
      window.confirm(
        `${label} for ${client.firstName} ${client.lastName}?\n\nThis creates (or refreshes) their /portal/login credentials.`
      )
    ) {
      portalMutation.mutate({
        client,
        action: client.portalAccess ? 'reset-password' : 'enable',
      });
    }
  };

  const handleDisablePortal = (client: Client) => {
    if (
      window.confirm(
        `Disable portal access for ${client.firstName} ${client.lastName}? They will no longer be able to sign in.`
      )
    ) {
      portalMutation.mutate({ client, action: 'disable' });
    }
  };

  const handleCreateClient = () => {
    setSelectedClient(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleViewClient = (clientItem: Client) => {
    setSelectedClient(clientItem);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleEditClient = (clientItem: Client) => {
    setSelectedClient(clientItem);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleSaveClient = async (clientData: Partial<Client>) => {
    if (dialogMode === 'create') {
      await createMutation.mutateAsync(clientData);
    } else if (dialogMode === 'edit' && selectedClient) {
      await updateMutation.mutateAsync({ id: selectedClient.id, clientData });
    }
  };

  const handleModeChange = (newMode: 'view' | 'edit' | 'create') => {
    setDialogMode(newMode);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'prospect':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getClientTypeColor = (clientType: string) => {
    switch (clientType) {
      case 'individual':
        return 'primary';
      case 'corporation':
        return 'secondary';
      case 'partnership':
        return 'info';
      case 'llc':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not provided';
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  const getClientInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (error) {
    console.error('Clients error:', error);
  }

  const clients = data?.data?.clients || [];

  return (
    <Box>
      {error && (
        <ListLoadErrorAlert
          resourceLabel="clients"
          onRetry={() => refetch()}
        />
      )}
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Clients
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateClient}
          >
            New Client
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage your client relationships and contact information.
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search clients..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="prospect">Prospect</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Client Type</InputLabel>
                <Select
                  value={filters.clientType}
                  onChange={(e) => setFilters({ ...filters, clientType: e.target.value })}
                  label="Client Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="individual">Individual</MenuItem>
                  <MenuItem value="corporation">Corporation</MenuItem>
                  <MenuItem value="partnership">Partnership</MenuItem>
                  <MenuItem value="llc">LLC</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  setFilters({ status: '', clientType: '', search: '' });
                  refetch();
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Portal</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No clients found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                  clients.map((client) => (
                    <TableRow key={client.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, bgcolor: theme.palette.primary.main }}>
                            {getClientInitials(client.firstName || '', client.lastName || '')}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {client.firstName || 'Unknown'} {client.lastName || 'Client'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {client.id?.slice(0, 8) || 'Unknown'}...
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Email sx={{ fontSize: 16, mr: 1, color: 'action.active' }} />
                            {client.email || 'No email'}
                          </Typography>
                          {client.phone && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                              <Phone sx={{ fontSize: 16, mr: 1, color: 'action.active' }} />
                              {client.phone}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={client.clientType || 'Unknown'}
                          color={getClientTypeColor(client.clientType || 'individual') as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={client.status || 'Unknown'}
                          color={getStatusColor(client.status || 'active') as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={client.portalAccess ? 'Enabled' : 'Off'}
                          color={client.portalAccess ? 'success' : 'default'}
                          size="small"
                          variant={client.portalAccess ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(client.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {canManagePortal && (
                          <Tooltip title={client.portalAccess ? 'Issue new temporary portal password' : 'Enable portal access & create login'}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={!client.email || portalMutation.isPending}
                                onClick={() => handleEnablePortal(client)}
                              >
                                {client.portalAccess ? <VpnKey /> : <LockOpen />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canManagePortal && client.portalAccess && (
                          <Tooltip title="Disable portal access">
                            <IconButton
                              size="small"
                              disabled={portalMutation.isPending}
                              onClick={() => handleDisablePortal(client)}
                            >
                              <Lock />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleViewClient(client)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handleEditClient(client)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(client.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Client Dialog */}
      <ClientDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        clientData={selectedClient}
        mode={dialogMode}
        onSave={handleSaveClient}
        onModeChange={handleModeChange}
      />

      <Dialog open={Boolean(portalCreds)} onClose={() => setPortalCreds(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Client portal credentials</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Share these with <strong>{portalCreds?.clientName}</strong>. They sign in at the
            client portal — not the staff login.
          </Alert>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Portal login URL
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: 'monospace', flex: 1 }}>
              {portalCreds?.portalLoginUrl}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                if (portalCreds?.portalLoginUrl) {
                  navigator.clipboard.writeText(portalCreds.portalLoginUrl);
                  toast.success('URL copied');
                }
              }}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Box>
          {portalCreds?.inviteUrl ? (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Invite / set-password link
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    flex: 1,
                    fontSize: '0.85rem',
                    wordBreak: 'break-all',
                  }}
                >
                  {portalCreds.inviteUrl}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (portalCreds?.inviteUrl) {
                      navigator.clipboard.writeText(portalCreds.inviteUrl);
                      toast.success('Invite link copied');
                    }
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Prefer sharing this link — the client sets their own password (expires in 48 hours).
              </Alert>
            </>
          ) : null}
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Email
          </Typography>
          <Typography sx={{ mb: 2, fontFamily: 'monospace' }}>{portalCreds?.email}</Typography>
          {portalCreds?.temporaryPassword ? (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Temporary password (fallback)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ fontFamily: 'monospace', flex: 1, fontSize: '1.1rem', fontWeight: 600 }}>
                  {portalCreds.temporaryPassword}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (portalCreds?.temporaryPassword) {
                      navigator.clipboard.writeText(portalCreds.temporaryPassword);
                      toast.success('Password copied');
                    }
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
              <Alert severity="warning">
                Fallback only if the invite link is not usable. Expires in 24 hours.
              </Alert>
            </>
          ) : (
            <Alert severity={portalCreds?.emailSent ? 'success' : 'warning'}>
              {portalCreds?.emailSent
                ? 'Invite link was emailed to the client.'
                : 'Credentials were generated but could not be shown (production mode). Ensure email delivery is configured.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setPortalCreds(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientsPage; 